import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper } from 'services/database-wrapper';
import { asciiToHex, AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { AppEnvironment } from 'environments/environment';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import {
  CoachPresenceDoc,
  COACH_PRESENCE_CLIENT_ID,
  PRESENCE_ONLINE_THRESHOLD_MS,
} from '@cf-shared/data-model/coach';
import {
  PresenceState,
  StillClaimedCheck,
  computeOnlineState,
  describeSkippedAuthDenial,
  formatTimeSince,
  judgeAuthDenialAsync,
} from 'services/coach-presence-helpers';

export { PresenceState } from 'services/coach-presence-helpers';

const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Heartbeat-based "who's online" presence between coach and client. Each side
 * writes a presence doc into the client's CouchDB every ~60s while "active";
 * consumers read `lastSeenUtc` and treat anything older than
 * PRESENCE_ONLINE_THRESHOLD_MS as offline. Full design:
 * `design-docs/coach-mode.md` → "Presence / Who's Online".
 */
@Injectable({ providedIn: 'root' })
export class CoachPresenceService {
  private heartbeatTimers = new Map<string, ReturnType<typeof setInterval>>();
  // serializes in-flight writes per docId so rapid start/stop or overlapping
  // intervals don't produce two concurrent `saveRecordAsync` chains for the
  // same _id (which would trigger database-wrapper's identity-map warning
  // when both sides instantiate a fresh object after a 404 miss)
  private heartbeatWriteChain = new Map<string, Promise<void>>();

  constructor(
    private userDb: UserDb,
    private auth: AuthService,
    private logging: LoggingService,
  ) {}

  /** Begin writing a presence heartbeat doc every 60s into the current user DB. */
  startHeartbeat(docId: string, displayName: string): void {
    this.stopHeartbeat(docId);
    void this.writeHeartbeatAsync(docId, displayName);
    const timer = setInterval(() => {
      void this.writeHeartbeatAsync(docId, displayName);
    }, HEARTBEAT_INTERVAL_MS);
    this.heartbeatTimers.set(docId, timer);
  }

  /** Cancel the heartbeat timer for a single docId (stops further writes; prior doc is left in place, staleness handles offline). */
  stopHeartbeat(docId: string): void {
    const timer = this.heartbeatTimers.get(docId);
    if (timer) clearInterval(timer);
    this.heartbeatTimers.delete(docId);
  }

  /** Cancel every heartbeat timer — used from app shutdown / logout paths. */
  stopAllHeartbeats(): void {
    for (const docId of Array.from(this.heartbeatTimers.keys())) {
      this.stopHeartbeat(docId);
    }
  }

  /**
   * Serialize heartbeat writes per docId via an in-flight promise chain.
   * Public (internal) facade over `writeHeartbeatOnceAsync` — each call
   * chains onto the previous in-flight write so rapid start/stop or
   * overlapping interval ticks don't produce two concurrent save chains
   * for the same `_id` (which would trigger database-wrapper's
   * identity-map warning on a 404 miss).
   */
  private writeHeartbeatAsync(docId: string, displayName: string): Promise<void> {
    // chain onto any in-flight write for this docId so saves serialize
    const prior = this.heartbeatWriteChain.get(docId) ?? Promise.resolve();
    const next = prior.then(() => this.writeHeartbeatOnceAsync(docId, displayName));
    this.heartbeatWriteChain.set(docId, next);
    // when this link settles, clear the chain tip iff no newer writer replaced it
    void next.finally(() => {
      if (this.heartbeatWriteChain.get(docId) === next) {
        this.heartbeatWriteChain.delete(docId);
      }
    });
    return next;
  }

  /**
   * Single heartbeat write pass: load the existing presence doc (or
   * construct a fresh one on 404), stamp `lastSeenUtc` + `displayName`,
   * save. PouchDB-not-found on first write is an expected path; other
   * read errors log transiently. A persistent save failure (auth
   * revoked, quota, conflict) is a non-transient signal and gets
   * routed through `LoggingService.trackSilentProblem` so telemetry
   * can surface sustained write denial.
   */
  private async writeHeartbeatOnceAsync(docId: string, displayName: string): Promise<void> {
    const database = this.userDb.database;
    if (!database) return;

    let doc: CoachPresenceDoc;
    try {
      doc = await database.withDocWatcherAsync(
        watcher => database.getTypedRecordAsync<CoachPresenceDoc>(docId, watcher));
    } catch (err) {
      if (!isPouchNotFound(err)) {
        // our own teardown (sign-out, most obviously) is not a fault — the same rule as the
        // save below
        if (isDatabaseTornDown(err)) return;
        // this is a LOCAL read, so being offline does not cause it. A read that keeps failing
        // also stops the save from ever running, which silences the save report below — so it
        // must be reported on its own, or the coach's view of this client goes stale with no
        // trace (#5191). Next interval retries regardless.
        this.logging.trackSilentProblem('coach-presence: heartbeat read failed', {
          docId,
          error: err?.message || String(err),
        });
        return;
      }
      doc = { _id: docId, lastSeenUtc: '', displayName } as CoachPresenceDoc;
    }

    doc.lastSeenUtc = DateTime.utc().toISO();
    doc.displayName = displayName;
    try {
      await database.saveRecordAsync(doc, DatabaseWrapper.getTransactionId());
    } catch (err) {
      // the heartbeat runs on a timer, so it keeps firing while the database is being torn
      // down — at sign-out, most obviously. A write refused for that reason is our own
      // teardown and is not a fault, so stay quiet about it (df-6ce1f9d6830e). Reporting it
      // would bury the real failures this branch exists for, and `trackSilentProblem` writes
      // a `console.error` line that the E2E unexpected-browser-error gate fails on.
      if (isDatabaseTornDown(err)) return;
      // a save failure is typically non-transient (auth revoked, quota,
      // persistent conflict) — route to telemetry so sustained denial
      // is visible. Next interval retries regardless.
      this.logging.trackSilentProblem('coach-presence: heartbeat save failed', {
        docId,
        error: err?.message || String(err),
      });
    }
  }

  /**
   * Watch a presence doc on the local DB's live `_changes` feed. Callback
   * fires on every change and on a scheduled staleness timer (so the UI flips
   * from online to offline purely from elapsed time, without needing another
   * sync event). Returns a cleanup function.
   */
  watchPresence(
    docId: string,
    callback: (state: PresenceState) => void,
  ): () => void {
    const database = this.userDb.database;
    if (!database) {
      callback({ online: false });
      return () => {};
    }

    let destroyed = false;
    let current: CoachPresenceDoc | null = null;
    let staleTimer: ReturnType<typeof setTimeout> | null = null;

    const emit = () => {
      if (destroyed) return;
      callback(computeOnlineState(current));
    };

    const scheduleStaleCheck = () => {
      if (staleTimer) {
        clearTimeout(staleTimer);
        staleTimer = null;
      }
      if (destroyed || !current?.lastSeenUtc) return;
      const ageMs = DateTime.utc().diff(DateTime.fromISO(current.lastSeenUtc)).as('milliseconds');
      const remaining = PRESENCE_ONLINE_THRESHOLD_MS - ageMs;
      if (remaining <= 0) return;
      staleTimer = setTimeout(() => {
        staleTimer = null;
        emit();
      }, remaining + 1000);
    };

    const reload = async () => {
      try {
        const doc = await database.getTypedRecordAsync<CoachPresenceDoc>(docId, watcher);
        // if the watcher was destroyed mid-fetch, bail before installing state
        // or scheduling the next staleness check (would otherwise outlive cleanup)
        if (destroyed) return;
        current = doc;
      } catch (err) {
        if (destroyed) return;
        // not-found only means the client has not written a heartbeat yet, and our own
        // teardown is not a fault. Anything else leaves the coach looking at "offline" for a
        // client whose presence we could not read, so it is reported (#5191)
        if (!isPouchNotFound(err) && !isDatabaseTornDown(err)) {
          this.logging.trackSilentProblem('coach-presence: watch reload failed', {
            docId,
            error: err?.message || String(err),
          });
        }
        current = null;
      }
      emit();
      scheduleStaleCheck();
    };

    // callback filters on docId for BOTH DocChangeType.Changed and .New. The
    // `New` path is load-bearing: until the first successful reload registers
    // docId via getTypedRecordAsync, sync-arriving presence docs reach us only
    // via the broadcast-to-all-watchers `New` notification.
    const watcher = database.createDocWatcher((changes) => {
      if (destroyed) return undefined;
      if (changes.some(c => c.id === docId)) {
        // #4358: RETURN it, never `void` it — the watcher uses the promise to
        // keep two reloads from overlapping. Cheap here (it filters to one
        // document id), but the rule is the rule: a `void` hides the work from
        // the watcher, and per-callsite exceptions are what this contract exists
        // to stop.
        return reload();
      }
      return undefined;
    }, { label: 'coach-presence' });

    void reload();

    return () => {
      destroyed = true;
      if (staleTimer) clearTimeout(staleTimer);
      watcher.destroy();
    };
  }

  /**
   * One-off authenticated GET of a remote client's presence doc. Used by the
   * coach-settings list, where the coach isn't in manage mode and has no live
   * feed on the client DB. `stillClaimedAsync`, when given, is asked on a 401/403
   * so a denial for a client the coach has already removed is not reported. A denial
   * while staff impersonate the coach is never reported (see `judgeAuthDenialAsync`).
   */
  async readPresenceOnce(clientUid: string, stillClaimedAsync?: StillClaimedCheck): Promise<PresenceState> {
    try {
      const remoteDbName = 'userdb-' + asciiToHex(clientUid);
      const url = AppEnvironment.pouchConfig.couch_serverurl
        + remoteDbName + '/' + encodeURIComponent(COACH_PRESENCE_CLIENT_ID);
      const fetchFn = this.auth.getFetchFunction();
      const response = await fetchFn(url, { method: 'GET' });
      if (response.status === 404) return { online: false };
      if (!response.ok) {
        // 401/403 for a client the coach still has means coach access was lost —
        // surface to telemetry so persistent failures are visible (unlike a
        // transient 5xx, which the next 60s poll retries). Judged in the
        // background: the check can wait for the initial sync, and the
        // offline answer below must not wait with it
        if (response.status === 401 || response.status === 403) {
          void this.reportAuthDenialAsync(clientUid, response.status, stillClaimedAsync);
        } else {
          console.log(`coach-presence: readPresenceOnce for ${clientUid} returned ${response.status}`);
        }
        return { online: false };
      }
      let doc: CoachPresenceDoc;
      try {
        doc = await response.json() as CoachPresenceDoc;
      } catch (parseErr) {
        // if the connection dropped or timed out while the body was being read, that is the
        // same network blip the quiet catch below exists for — hand it on to that catch
        if (!(parseErr instanceof SyntaxError)) throw parseErr;
        // the server answered 2xx with a body that is not JSON. That is not a network blip,
        // so it is reported rather than sharing the quiet catch below (#5191)
        this.logging.trackSilentProblem('coach-presence: readPresenceOnce unreadable body', {
          clientUid,
          error: parseErr?.message || String(parseErr),
        });
        return { online: false };
      }
      return computeOnlineState(doc);
    } catch (err) {
      // not reported: a network failure of a 60 s poll — the next poll retries, and a coach on
      // a bad connection would otherwise file an event a minute. Revoked access (401/403) and
      // an unreadable body are reported above
      console.log(`coach-presence: readPresenceOnce failed for ${clientUid}:`, err?.message || err);
      return { online: false };
    }
  }

  /** Report a presence-read 401/403 unless it is expected (see `judgeAuthDenialAsync`). Never throws. */
  private async reportAuthDenialAsync(clientUid: string, status: number, stillClaimedAsync?: StillClaimedCheck): Promise<void> {
    const verdict = await judgeAuthDenialAsync(this.userDb.isImpersonating(), stillClaimedAsync);
    if (!verdict.report) {
      // staff impersonating the coach, or a client the coach has removed: the server is right to refuse
      console.log(`coach-presence: readPresenceOnce for ${clientUid} denied (${status}); ${describeSkippedAuthDenial(verdict.skipReason)}`);
      return;
    }
    this.logging.trackSilentProblem('coach-presence: readPresenceOnce auth denied', {
      clientUid,
      status,
      ...(verdict.checkError ? { stillClaimedCheckError: verdict.checkError } : {}),
    });
  }

  /** Instance-method form of `formatTimeSince` — kept for use via the Angular-injected service. */
  getTimeSinceString(lastSeenUtc: string): string {
    return formatTimeSince(lastSeenUtc);
  }
}
